import { Injectable } from '@angular/core';
import { ApiService } from '../../api/api.service';
import { apiUrls } from '../../../config/api/api-urls.config';
import { NewsResultsResponse } from '../../../models/news/responses/api-news-results-response.model';
import { NewsTranslation } from '../../../models/news/news-translation.model';
import { NewsPredefinedResultsResponse } from '../../../models/news/responses/api-news-predefined-results-response.model';
import { NewsUserResultsResponse } from '../../../models/news/responses/api-news-user-results-response.model';
import { NewsReward } from '../../../models/news/news-reward.model';

@Injectable({
  providedIn: 'root'
})
export class ApiNewsService {

  constructor(private apiService: ApiService) { }

  getNews(page: number, resultCount: number): Promise<NewsResultsResponse> {
    return new Promise<NewsResultsResponse>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.newsGetNewsUrl, { page, result_count: resultCount }, NewsResultsResponse).then(
        (data: NewsResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  addNews(name: string, startTimestamp: number, endTimestamp: number, isActive: boolean, isRate: boolean, translations: NewsTranslation[], userCreatedAfterTimestamp: number | null = null, userCreatedBeforeTimestamp: number | null = null, newsPredefinedId: number = 0, userId: number = 0, periodicity: object | null = null, linkDefault: string = '', linkIos: string = '', linkAndroid: string = ''): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = {
        name,
        start_timestamp: startTimestamp,
        end_timestamp: endTimestamp,
        is_active: isActive,
        is_rate: isRate,
        user_id: userId,
        news_predefined_id: newsPredefinedId,
        user_created_after_timestamp: userCreatedAfterTimestamp,
        user_created_before_timestamp: userCreatedBeforeTimestamp,
        link_default: linkDefault,
        link_ios: linkIos,
        link_android: linkAndroid,
        translations: translations.map(t => ({ title: t.title, summary: t.summary, content: t.content, link_button_name: t.linkButtonName, language_id: t.languageId })),
        periodicity
      };

      this.apiService.launchAdminApiRequestPost(apiUrls.newsAddNewsUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  editNews(newsId: number, name: string, startTimestamp: number, endTimestamp: number, isActive: boolean, isRate: boolean, translations: NewsTranslation[], userCreatedAfterTimestamp: number | null = null, userCreatedBeforeTimestamp: number | null = null, newsPredefinedId: number = 0, userId: number = 0, periodicity: object | null = null, linkDefault: string = '', linkIos: string = '', linkAndroid: string = ''): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = {
        news_id: newsId,
        name,
        start_timestamp: startTimestamp,
        end_timestamp: endTimestamp,
        is_active: isActive,
        is_rate: isRate,
        user_id: userId,
        news_predefined_id: newsPredefinedId,
        user_created_after_timestamp: userCreatedAfterTimestamp,
        user_created_before_timestamp: userCreatedBeforeTimestamp,
        link_default: linkDefault,
        link_ios: linkIos,
        link_android: linkAndroid,
        translations: translations.map(t => ({ title: t.title, summary: t.summary, content: t.content, link_button_name: t.linkButtonName, language_id: t.languageId })),
        periodicity
      };

      this.apiService.launchAdminApiRequestPost(apiUrls.newsEditNewsUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  deleteNews(newsId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.newsDeleteNewsUrl, { news_id: newsId }).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  changeNewsActiveState(newsId: number, isActive: boolean): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.newsChangeActiveStateUrl, { news_id: newsId, is_active: isActive }).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  addNewsReward(newsId: number, currencyType: number, currencyQuantity: number, currencyTicketCost: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = { news_id: newsId, currency_type: currencyType, currency_quantity: currencyQuantity, currency_ticket_cost: currencyTicketCost };
      this.apiService.launchAdminApiRequestPost(apiUrls.newsAddNewsRewardUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  editNewsReward(rewardId: number, currencyType: number, currencyQuantity: number, currencyTicketCost: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = { reward_id: rewardId, currency_type: currencyType, currency_quantity: currencyQuantity, currency_ticket_cost: currencyTicketCost };
      this.apiService.launchAdminApiRequestPost(apiUrls.newsEditNewsRewardUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  deleteNewsReward(rewardId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.newsDeleteNewsRewardUrl, { reward_id: rewardId }).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  getNewsPredefined(page: number, resultCount: number): Promise<NewsPredefinedResultsResponse> {
    return new Promise<NewsPredefinedResultsResponse>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.newsGetNewsPredefinedUrl, { page, result_count: resultCount }, NewsPredefinedResultsResponse).then(
        (data: NewsPredefinedResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  addNewsPredefined(name: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.newsAddNewsPredefinedUrl, { name }).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  editNewsPredefined(newsPredefinedId: number, name: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.newsEditNewsPredefinedUrl, { news_predefined_id: newsPredefinedId, name }).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  deleteNewsPredefined(newsPredefinedId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.newsDeleteNewsPredefinedUrl, { news_predefined_id: newsPredefinedId }).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  editNewsPredefinedTranslation(newsPredefinedId: number, languageId: number, title: string, summary: string, content: string, linkButtonName: string = ''): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = { news_predefined_id: newsPredefinedId, language_id: languageId, title, summary, content, link_button_name: linkButtonName };
      this.apiService.launchAdminApiRequestPost(apiUrls.newsEditNewsPredefinedTranslationUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  deleteNewsPredefinedTranslation(newsPredefinedId: number, languageId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.newsDeleteNewsPredefinedTranslationUrl, { news_predefined_id: newsPredefinedId, language_id: languageId }).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  getNewsUser(userId: number, page: number, resultCount: number): Promise<NewsUserResultsResponse> {
    return new Promise<NewsUserResultsResponse>((resolve, reject) => {
      this.apiService.launchAdminApiRequestPost(apiUrls.newsGetNewsUserUrl, { user_id: userId, page, result_count: resultCount }, NewsUserResultsResponse).then(
        (data: NewsUserResultsResponse) => { resolve(data); }, error => { reject(error); });
    });
  }

  addNewsUser(userId: number, newsPredefinedId: number | null, title: string, summary: string, content: string, isRate: boolean, rewards: NewsReward[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = {
        user_id: userId,
        news_predefined_id: newsPredefinedId,
        title,
        summary,
        content,
        is_rate: isRate,
        rewards: rewards.map(r => ({ currency_type: r.currencyType, currency_quantity: r.currencyQuantity, currency_ticket_cost: r.currencyTicketCost }))
      };
      this.apiService.launchAdminApiRequestPost(apiUrls.newsAddNewsUserUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }

  editNewsUser(newsId: number, newsPredefinedId: number | null, title: string, summary: string, content: string, isRate: boolean): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const body = { news_id: newsId, news_predefined_id: newsPredefinedId, title, summary, content, is_rate: isRate };
      this.apiService.launchAdminApiRequestPost(apiUrls.newsEditNewsUserUrl, body).then(
        () => { resolve(); }, error => { reject(error); });
    });
  }
}
